import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Step,
  StepDescription,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  StepTitle,
  Stepper,
  Tag,
  Text,
} from "@chakra-ui/react";
import { FormattedMessage } from "react-intl";

import { AnimateSize } from "@nens/chakra/components";
import { JobRead, Log } from "@nens/rana/api-v1";
import { capitalizeFirst } from "@nens/shared-utils";

import { useJobLogsQuery } from "@/queries/query-process-logs";
import { jobDuration } from "@/utils/job-duration";

import { jobIsRunning } from "../../../utils/job-is-running";
import { ProcessRunChildProps } from "../types/process-props";

const getInnerHtml = (text: string | undefined | null) =>
  text
    ? {
        __html: text.split("\n").join("<br />"),
      }
    : undefined;

export function ProcessRunStatusLogs(props: ProcessRunChildProps) {
  const { job } = props;

  return (
    <AnimateSize
      dontAnimate={job && !jobIsRunning(job.state.type)}
      defaultToBottom
      px={6}
      maxH="sm"
      overflow="auto"
    >
      {job && <ProcessRunStatusLogsItems {...props} job={job} />}
    </AnimateSize>
  );
}

export interface ProcessRunStatusLogsItemsProps extends ProcessRunChildProps {
  job: NonNullable<ProcessRunChildProps["job"]>;
}

function ProcessRunStatusLogsItems({ job }: ProcessRunStatusLogsItemsProps) {
  const jobLogs = useJobLogsQuery(job);

  if (!jobLogs.data?.items.length) return null;

  return (
    <Stepper
      index={jobLogs.data?.items.length ?? 0}
      orientation="vertical"
      gap="0"
      py={4}
      size="sm"
    >
      {jobLogs.data?.items.map((log, idx) => (
        <RenderStep key={idx} job={job} log={log} />
      ))}
    </Stepper>
  );
}

type RenderStepProps = {
  log: Log;
  job: JobRead;
};

export const RenderStep = ({ job, log }: RenderStepProps) => {
  const { message, exception, timestamp } = log;
  const isError = !!exception;
  const duration = jobDuration(job.createdAt, timestamp);

  return (
    <Step>
      <StepIndicator bg={isError ? "semantic.error.main !important" : undefined}>
        <StepStatus complete={<StepNumber />} incomplete={<StepNumber />} active={<StepNumber />} />
      </StepIndicator>
      <Box mb={4}>
        <StepTitle>
          <Text
            fontWeight={isError ? 600 : undefined}
            dangerouslySetInnerHTML={getInnerHtml(
              isError
                ? `${capitalizeFirst(exception.errorType)} error: ${exception.title}`
                : message
            )}
          />
        </StepTitle>
        <StepDescription>
          {isError && (
            <>
              <Text fontSize="sm" dangerouslySetInnerHTML={getInnerHtml(exception.description)} />

              <Accordion pt={1} pb={2} allowToggle>
                <AccordionItem>
                  {({ isExpanded }) => (
                    <>
                      <AccordionButton
                        textDecor="underline"
                        fontSize="sm"
                        _hover={{ bg: "inherit" }}
                        p={0}
                      >
                        <AccordionIcon />
                        {isExpanded ? (
                          <FormattedMessage
                            id="process.status.hide_technical"
                            defaultMessage="Hide technical details"
                          />
                        ) : (
                          <FormattedMessage
                            id="process.status.show_technical"
                            defaultMessage="Show technical details"
                          />
                        )}
                      </AccordionButton>
                      <AccordionPanel>
                        <Text
                          fontSize="sm"
                          dangerouslySetInnerHTML={getInnerHtml(exception.traceback)}
                        />
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>
              </Accordion>
            </>
          )}
          <Tag size="sm">{duration}</Tag>
        </StepDescription>
      </Box>
      <StepSeparator />
    </Step>
  );
};
